import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';

// Note: Backend now returns fully qualified URLs for branding images

const Apply = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState(null);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    username: '',
    first_name: '',
    last_name: '',
    title: 'Dr',
    gender: 'Male',
    email: '',
    confirm_email: '',
    contact_number: '',
    password: '',
    work_station: '',
    specialty: '',
    residential_address: '',
    date_of_birth: '',
    nrc_passport: '',
    next_of_kin: {
      surname: '',
      first_name: '',
      gender: 'Male',
      relationship: '',
      residential_address: '',
      contact_number: '',
      email: ''
    },
    beneficiaries: []  // Start empty - beneficiaries are optional
  });

  const [files, setFiles] = useState({
    id_copy: null,
    portrait_photo: null,
    proof_of_payment: null
  });

  useEffect(() => {
    loadBranding();
  }, []);

  const loadBranding = async () => {
    try {
      const response = await apiClient.get('/settings/branding');
      setBranding(response.data);
    } catch (error) {
      // Use defaults if API fails
      setBranding({ app_name: 'MemberHub' });
    }
  };

  const addBeneficiary = () => {
    if (formData.beneficiaries.length < 5) {
      setFormData({
        ...formData,
        beneficiaries: [...formData.beneficiaries, { full_name: '', date_of_birth: '', age: 0, relationship: '' }]
      });
    } else {
      toast.error('Maximum 5 beneficiaries allowed');
    }
  };

  const removeBeneficiary = (index) => {
    const newBeneficiaries = formData.beneficiaries.filter((_, i) => i !== index);
    setFormData({ ...formData, beneficiaries: newBeneficiaries });
  };

  const updateBeneficiary = (index, field, value) => {
    const newBeneficiaries = [...formData.beneficiaries];
    newBeneficiaries[index] = { ...newBeneficiaries[index], [field]: value };
    
    // Auto-calculate age from date of birth
    if (field === 'date_of_birth' && value) {
      const birthDate = new Date(value);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      newBeneficiaries[index].age = age;
    }
    
    setFormData({ ...formData, beneficiaries: newBeneficiaries });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.email !== formData.confirm_email) {
      toast.error('Emails do not match');
      return;
    }

    if (!files.id_copy || !files.portrait_photo || !files.proof_of_payment) {
      toast.error('Please upload all required files');
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();
      submitData.append('application_data', JSON.stringify(formData));
      submitData.append('id_copy', files.id_copy);
      submitData.append('portrait_photo', files.portrait_photo);
      submitData.append('proof_of_payment', files.proof_of_payment);

      await apiClient.post('/applications/submit', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Application submitted successfully!');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  const appName = branding?.app_name || 'MemberHub';

  return (
    <div className="min-h-screen bg-background">
      {/* Header with Navigation */}
      <header className="border-b border-border bg-card sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            {branding?.logo_url && branding?.use_logo_in_header ? (
              <img src={branding.logo_url} alt={appName} className="h-8 w-auto" />
            ) : (
              <span className="text-2xl font-heading font-bold text-foreground">{appName}</span>
            )}
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" data-testid="back-to-home-button">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" data-testid="login-link-button">Login</Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-heading font-bold mb-2">Membership Application</h1>
          <p className="text-muted-foreground">Step {step} of 4 - Complete all sections to apply</p>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center mb-8 gap-2">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`h-2 w-16 rounded-full transition-colors ${
                s <= step ? 'bg-primary' : 'bg-muted'
              }`}
            />
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              {step === 1 && 'Personal Information'}
              {step === 2 && 'Next of Kin Details'}
              {step === 3 && 'Beneficiaries Information (Optional)'}
              {step === 4 && 'Upload Documents'}
            </CardTitle>
            <CardDescription>
              {step === 1 && 'Provide your personal details'}
              {step === 2 && 'Emergency contact information'}
              {step === 3 && 'Add beneficiaries (optional - you can add them later)'}
              {step === 4 && 'Upload required documents (PDF, JPG, PNG)'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Step 1: Personal Information */}
              {step === 1 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Username *</Label>
                      <Input required value={formData.username} onChange={(e) => setFormData({...formData, username: e.target.value})} data-testid="username-input" />
                    </div>
                    <div>
                      <Label>Title *</Label>
                      <select required className="w-full border rounded p-2 h-10" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} data-testid="title-select">
                        <option>Dr</option>
                        <option>Prof</option>
                        <option>Mr</option>
                        <option>Mrs</option>
                        <option>Ms</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>First Name *</Label>
                      <Input required value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} data-testid="first-name-input" />
                    </div>
                    <div>
                      <Label>Last Name *</Label>
                      <Input required value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} data-testid="last-name-input" />
                    </div>
                  </div>

                  <div>
                    <Label>Gender *</Label>
                    <select required className="w-full border rounded p-2 h-10" value={formData.gender} onChange={(e) => setFormData({...formData, gender: e.target.value})} data-testid="gender-select">
                      <option>Male</option>
                      <option>Female</option>
                      <option>Other</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Email *</Label>
                      <Input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} data-testid="email-input" />
                    </div>
                    <div>
                      <Label>Confirm Email *</Label>
                      <Input type="email" required value={formData.confirm_email} onChange={(e) => setFormData({...formData, confirm_email: e.target.value})} data-testid="confirm-email-input" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Contact Number *</Label>
                      <Input required value={formData.contact_number} onChange={(e) => setFormData({...formData, contact_number: e.target.value})} data-testid="contact-number-input" />
                    </div>
                    <div>
                      <Label>Password *</Label>
                      <Input type="password" required value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} data-testid="password-input" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Work Station *</Label>
                      <Input required value={formData.work_station} onChange={(e) => setFormData({...formData, work_station: e.target.value})} data-testid="work-station-input" />
                    </div>
                    <div>
                      <Label>Specialty *</Label>
                      <Input required value={formData.specialty} onChange={(e) => setFormData({...formData, specialty: e.target.value})} data-testid="specialty-input" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Date of Birth *</Label>
                      <Input type="date" required value={formData.date_of_birth} onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})} data-testid="dob-input" />
                    </div>
                    <div>
                      <Label>NRC/Passport *</Label>
                      <Input required value={formData.nrc_passport} onChange={(e) => setFormData({...formData, nrc_passport: e.target.value})} data-testid="nrc-input" />
                    </div>
                  </div>

                  <div>
                    <Label>Residential Address *</Label>
                    <Input required value={formData.residential_address} onChange={(e) => setFormData({...formData, residential_address: e.target.value})} data-testid="address-input" />
                  </div>

                  <Button type="button" onClick={() => setStep(2)} className="w-full" data-testid="next-to-kin-button">
                    Next: Next of Kin
                  </Button>
                </div>
              )}

              {/* Step 2: Next of Kin */}
              {step === 2 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Surname *</Label>
                      <Input required value={formData.next_of_kin.surname} onChange={(e) => setFormData({...formData, next_of_kin: {...formData.next_of_kin, surname: e.target.value}})} data-testid="kin-surname-input" />
                    </div>
                    <div>
                      <Label>First Name *</Label>
                      <Input required value={formData.next_of_kin.first_name} onChange={(e) => setFormData({...formData, next_of_kin: {...formData.next_of_kin, first_name: e.target.value}})} data-testid="kin-first-name-input" />
                    </div>
                  </div>

                  <div>
                    <Label>Gender *</Label>
                    <select required className="w-full border rounded p-2 h-10" value={formData.next_of_kin.gender} onChange={(e) => setFormData({...formData, next_of_kin: {...formData.next_of_kin, gender: e.target.value}})}>
                      <option>Male</option>
                      <option>Female</option>
                      <option>Other</option>
                    </select>
                  </div>

                  <div>
                    <Label>Relationship *</Label>
                    <Input required placeholder="e.g., Spouse, Parent, Sibling" value={formData.next_of_kin.relationship} onChange={(e) => setFormData({...formData, next_of_kin: {...formData.next_of_kin, relationship: e.target.value}})} data-testid="kin-relationship-input" />
                  </div>

                  <div>
                    <Label>Residential Address *</Label>
                    <Input required value={formData.next_of_kin.residential_address} onChange={(e) => setFormData({...formData, next_of_kin: {...formData.next_of_kin, residential_address: e.target.value}})} data-testid="kin-address-input" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Contact Number *</Label>
                      <Input required value={formData.next_of_kin.contact_number} onChange={(e) => setFormData({...formData, next_of_kin: {...formData.next_of_kin, contact_number: e.target.value}})} data-testid="kin-contact-input" />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input type="email" required value={formData.next_of_kin.email} onChange={(e) => setFormData({...formData, next_of_kin: {...formData.next_of_kin, email: e.target.value}})} data-testid="kin-email-input" />
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Button type="button" onClick={() => setStep(1)} variant="outline" className="flex-1">
                      Back
                    </Button>
                    <Button type="button" onClick={() => setStep(3)} className="flex-1" data-testid="next-to-beneficiaries-button">
                      Next: Beneficiaries
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: Beneficiaries (Optional) */}
              {step === 3 && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>This step is optional.</strong> You can add beneficiaries now or skip this step and add them later from your member dashboard.
                    </p>
                  </div>

                  <div className="flex justify-between items-center mb-4">
                    <p className="text-sm text-muted-foreground">Add up to 5 beneficiaries (Beneficiary 1: max 70 years, Others: under 21 years)</p>
                    <Button type="button" onClick={addBeneficiary} variant="outline" size="sm" data-testid="add-beneficiary-button">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Beneficiary
                    </Button>
                  </div>

                  {formData.beneficiaries.length === 0 ? (
                    <div className="text-center py-8 border-2 border-dashed rounded-lg">
                      <p className="text-muted-foreground mb-4">No beneficiaries added yet</p>
                      <Button type="button" onClick={addBeneficiary} variant="outline" data-testid="add-first-beneficiary-button">
                        <Plus className="w-4 h-4 mr-2" />
                        Add a Beneficiary
                      </Button>
                    </div>
                  ) : (
                    formData.beneficiaries.map((ben, index) => (
                      <Card key={index} className="p-4">
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-semibold">Beneficiary {index + 1}</h4>
                          <Button type="button" onClick={() => removeBeneficiary(index)} variant="ghost" size="sm" data-testid={`remove-beneficiary-${index}`}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                        
                        <div className="space-y-3">
                          <div>
                            <Label>Full Name *</Label>
                            <Input required value={ben.full_name} onChange={(e) => updateBeneficiary(index, 'full_name', e.target.value)} data-testid={`beneficiary-name-${index}`} />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Date of Birth *</Label>
                              <Input type="date" required value={ben.date_of_birth} onChange={(e) => updateBeneficiary(index, 'date_of_birth', e.target.value)} data-testid={`beneficiary-dob-${index}`} />
                            </div>
                            <div>
                              <Label>Age (Auto-calculated)</Label>
                              <Input type="number" value={ben.age} readOnly className="bg-muted" />
                            </div>
                          </div>
                          <div>
                            <Label>Relationship *</Label>
                            <Input required placeholder="e.g., Child, Dependent" value={ben.relationship} onChange={(e) => updateBeneficiary(index, 'relationship', e.target.value)} data-testid={`beneficiary-relationship-${index}`} />
                          </div>
                        </div>
                      </Card>
                    ))
                  )}

                  <div className="flex gap-4 mt-6">
                    <Button type="button" onClick={() => setStep(2)} variant="outline" className="flex-1">
                      Back
                    </Button>
                    <Button type="button" onClick={() => setStep(4)} variant={formData.beneficiaries.length === 0 ? "secondary" : "default"} className="flex-1" data-testid="next-to-documents-button">
                      {formData.beneficiaries.length === 0 ? 'Skip & Continue' : 'Next: Documents'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 4: Upload Documents */}
              {step === 4 && (
                <div className="space-y-4">
                  <div>
                    <Label>Copy of ID *</Label>
                    <Input type="file" required accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFiles({...files, id_copy: e.target.files[0]})} data-testid="id-copy-upload" />
                    <p className="text-xs text-muted-foreground mt-1">Accepted: PDF, JPG, PNG (Max 5MB)</p>
                  </div>
                  
                  <div>
                    <Label>Portrait Photo *</Label>
                    <Input type="file" required accept=".jpg,.jpeg,.png" onChange={(e) => setFiles({...files, portrait_photo: e.target.files[0]})} data-testid="portrait-photo-upload" />
                    <p className="text-xs text-muted-foreground mt-1">Accepted: JPG, PNG (Max 5MB)</p>
                  </div>
                  
                  <div>
                    <Label>Proof of Payment *</Label>
                    <Input type="file" required accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFiles({...files, proof_of_payment: e.target.files[0]})} data-testid="proof-of-payment-upload" />
                    <p className="text-xs text-muted-foreground mt-1">Accepted: PDF, JPG, PNG (Max 5MB)</p>
                  </div>

                  <div className="bg-muted/50 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Review Your Application</h4>
                    <p className="text-sm text-muted-foreground">Please review all information before submitting. You can use the Back button to make changes.</p>
                  </div>

                  <div className="flex gap-4">
                    <Button type="button" onClick={() => setStep(3)} variant="outline" className="flex-1">
                      Back
                    </Button>
                    <Button type="submit" disabled={loading} className="flex-1" data-testid="submit-application-button">
                      {loading ? 'Submitting...' : 'Submit Application'}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Apply;